import { PAGE_NAMES } from '~/shared/constants';
import { changeAuthStatus } from '~/shared/helpers/cookie-helpers';
import { getUrl } from '~/shared/helpers/string-helpers';
import type { AppDispatch } from '~/shared/store';
import { graphqlApi } from '~/shared/store/api-services/graphql-api';
import { changeStateOfConfirmModal, setUser } from '~/shared/store/slices/app/app-slice';
import { setUserWithCourses } from '~/shared/store/slices/users/users-slice';
import { LOCALES, type Locale } from '~/universal/constants';

export const isBrowser = (
  currentWindow: Window | null | undefined = typeof window === 'undefined' ? undefined : window,
): boolean => currentWindow != null;

export const isValidMongoId = (value) => {
  return typeof value === 'string' && /^[a-f\d]{24}$/i.test(value);
};

export const assignLocation = (href: string): void => {
  window.location.assign(href);
};

export const goToBlankPage = ({ link, lng, alias }: { lng: Locale; link: string; alias: string }) => {
  const win = window.open(getLocaleLink(getUrl({ link, alias }), lng), '_blank');
  if (win != null) {
    win.focus();
  }
};

export const getLocaleLink = (link, lng = LOCALES.UA) => {
  return `/${lng}${link}`;
};

export const ensurePlayback = () => {
  try {
    (window as any).__appAudioCtx =
      (window as any).__appAudioCtx || new (window.AudioContext || (window as any).webkitAudioContext)();
    (window as any).__appAudioCtx?.resume?.();
  } catch {}

  document.querySelectorAll('video').forEach((v) => {
    try {
      (v as HTMLVideoElement).play().catch(() => {});
    } catch {}
  });
};

export const isItemSelected = (item: { key: string; activePaths?: string[] }, pathname) => {
  const homePaths = [PAGE_NAMES.HOME, `/${LOCALES.UA}`, `/${LOCALES.RU}`, `/${LOCALES.EN}`];

  if (homePaths.includes(item.key)) {
    return homePaths.includes(pathname);
  }

  if (item.activePaths?.length) {
    return item.activePaths.some((path) => pathname.startsWith(path));
  }

  return item.key === pathname;
};

export const onSignOut = ({
  currentUser,
  dispatch,
  userId,
  isClientsPanel,
  currentCourse,
  sessionKey,
  effectiveLng,
  t,
  rootContext,
  router,
  lng,
}) => {
  if (!currentUser && dispatch) {
    dispatch(setUser(null));
    dispatch(setUserWithCourses(null));
  }

  const navigationOnBehalfUserEndpoint = graphqlApi.endpoints.navigationOnBehalfUser as
    | {
        initiate: (args) => unknown;
      }
    | undefined;

  if (currentUser && navigationOnBehalfUserEndpoint && userId && sessionKey && dispatch) {
    dispatch(
      changeStateOfConfirmModal({
        onTrigger: async () => {
          const args = {
            _id: userId,
            //   course: currentCourse,
            sessionKey,
          };

          const thunk = navigationOnBehalfUserEndpoint.initiate(args);
          await dispatch(thunk as unknown as Parameters<AppDispatch>[0]);

          localStorage.removeItem('currentUser');
          localStorage.removeItem('currentCourse');
          localStorage.removeItem('sessionKey');

          window.location.href = getLocaleLink(
            getUrl({
              link: PAGE_NAMES.PLATFORM_TEACHERS,
              getParams: `?course=${currentCourse}`,
            }),
            effectiveLng,
          );
        },
        title: t('modal.exit-from-navigation-on-behalf.title'),
        message: t('modal.exit-from-navigation-on-behalf.message'),
        isShowed: true,
        isDisabledSubmitButton: false,
        isRemoving: false,
        itemKey: null,
      }),
    );
  } else {
    changeAuthStatus({
      isSignOut: true,
      isStayOnPage: false,
      rootContext,
      isClientsPanel,
      router,
      lng,
    });
  }
};
